'use client';

import React, { useState } from 'react';
import { signupStyles } from '../../signup-templates/signup.styles';
import {
   Box,
   Stack,
   Text,
   Button,
   Field,
   Link,
   Flex,
   Input,
   Dialog,
   Portal,
   IconButton,
   Image,
   Alert,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { submitApplicationSchema, TSubmitApplication } from './application.validator';
import { zodResolver } from '@hookform/resolvers/zod';
import { MdKeyboardArrowLeft, MdOutlineKeyboardArrowRight } from 'react-icons/md';
import { viewCardStyles } from '@/components/layouts/home/ViewCard/viewCard.styles';
import { createApplication } from '@/services/application/application.service';
import { Tooltip } from '@/components/ui';
import { InfoIcon } from '@/../public/assets/icons';
import { HiTrash } from 'react-icons/hi2';
import { Application } from '@/core/models/application';
import BarLoader from '@/components/ui/loaders/BarLoader';
import { destroySession } from '@/app/actions/auth/cookies.action';

const SubmitApplicationForm = ({
   setApplication,
}: {
   setApplication: React.Dispatch<React.SetStateAction<Application | null>>;
}) => {
   const [viewModal, setViewModal] = useState(false);
   const [carouselIndex, setCarouselIndex] = useState(0);
   // const [openSupportModal, setOpenSupportModal] = useState(false);
   const {
      register,
      handleSubmit,
      reset,
      formState: { errors, isSubmitting },
      watch,
      resetField,
      setError,
   } = useForm<TSubmitApplication>({
      mode: 'onChange',
      resolver: zodResolver(submitApplicationSchema),
   });

   const onSubmit = handleSubmit(async value => {
      const formData = new FormData();

      formData.append('card_photo', value.card_photo[0]);
      formData.append('summary_care', value.summary_care[0]);
      formData.append('medical_certificate', value.medical_certificate[0]);
      formData.append('qualifying_condition', value.qualifying_condition);

      const [error, data] = await createApplication(formData);

      if (error) {
         if (error.response?.status === 401) {
            setError('root', { message: 'Your session is timed out. Please login again', type: 'manual' });
            return;
         }
         setError('root', { message: error.response?.data.message as string, type: 'manual' });
         return console.log(error);
      }

      setApplication(data.response);
      await destroySession();
   });

   const { card_photo, summary_care, medical_certificate } = watch();

   const card_photo_url = card_photo?.[0] ? URL.createObjectURL(card_photo?.[0]) : '';
   const summary_care_url = summary_care?.[0] ? URL.createObjectURL(summary_care?.[0]) : '';
   const medical_certificate_url = medical_certificate?.[0] ? URL.createObjectURL(medical_certificate?.[0]) : '';

   const carouselItems = [
      { src: card_photo_url, alt: 'card_photo' },
      { src: summary_care_url, alt: 'summary_care' },
      { src: medical_certificate_url, alt: 'medical_certificate' },
   ];

   const viewItem = carouselItems[carouselIndex];

   const handleView = (isOpen: boolean, index?: number) => {
      setViewModal(isOpen);
      if (index !== undefined && index !== null) {
         setCarouselIndex(index);
      }
   };

   const prevFn = () => {
      if (carouselIndex > 0) setCarouselIndex(carouselIndex - 1);
   };

   const nextFn = () => {
      if (carouselIndex < carouselItems.length - 1) setCarouselIndex(carouselIndex + 1);
   };

   if (isSubmitting) return <BarLoader loadingText="Submitting application..." />;

   // const openModal = () => {
   //    setOpenSupportModal(true);
   // };

   // const handleSupportModalClose = () => {
   //    setOpenSupportModal(false);
   // };

   return (
      <Box as="form" css={signupStyles.signup_form} onSubmit={onSubmit}>
         {errors.root?.message && (
            <Alert.Root status="error">
               <Alert.Indicator />
               <Alert.Title>{errors.root.message}</Alert.Title>
            </Alert.Root>
         )}
         <Stack gap="4" align="flex-start" minW="full">
            <Box css={signupStyles.support_info}>
               <Text color="white" textStyle="xs" fontWeight="bold">
                  Need help getting your Summary of Care? We’re here to help. Email our Support Team and we’ll assist
                  you in requesting one from your GP.
               </Text>
               <Link
                  href="#"
                  onClick={() => {
                     window.location.href = 'mailto:support@example.com';
                  }}
                  color="black"
                  textStyle="xs"
                  fontWeight="bold"
                  h="7"
                  maxW="20"
                  minW="20"
                  p="0"
                  bg="brand"
                  rounded="12px"
                  justifyContent="center"
               >
                  Support
               </Link>
            </Box>
            <Field.Root>
               <Field.Label textStyle="xs" color="white" mb="1">
                  Upload your photo
                  <Box as="span" color="error">
                     *
                  </Box>
                  <Tooltip content={(errors.card_photo?.message as string) || ''} open={!!errors.card_photo?.message}>
                     <InfoIcon fill="#F5E876" width={16} height={16} />
                  </Tooltip>
               </Field.Label>
               <Flex
                  alignItems="center"
                  justifyContent="space-between"
                  border="2px dashed gray"
                  p={3}
                  borderRadius="lg"
                  color="white"
                  w="full"
               >
                  <Flex css={signupStyles.upload_btn_box} alignItems="center" gap="3">
                     <Image src="/assets/icons/scan_user.svg" alt="user icon" width="21" height="21" />
                     <Text fontSize="sm">Photo of yourself (Passport Style)</Text>
                  </Flex>
                  {card_photo_url ? (
                     <Box display="flex" gap="3" alignItems="center">
                        {!errors.card_photo?.message && (
                           <Button
                              bg="brand"
                              textStyle="xs"
                              color="black"
                              fontWeight="bold"
                              borderRadius="12px"
                              h="8"
                              maxW="4"
                              minW="4"
                              px="6"
                              onClick={() => handleView(true, 0)}
                           >
                              View
                           </Button>
                        )}
                        <IconButton
                           height="40px"
                           rounded="full"
                           bg="#B5B5B5"
                           color="white"
                           onClick={() => resetField('card_photo')}
                        >
                           <HiTrash />
                        </IconButton>
                     </Box>
                  ) : (
                     <Field.Label
                        color="black"
                        textStyle="xs"
                        fontWeight="bold"
                        h="7"
                        maxW="20"
                        minW="20"
                        p="0"
                        bg="brand"
                        rounded="12px"
                        justifyContent="center"
                        htmlFor="card_photo"
                        cursor="pointer"
                     >
                        Upload
                     </Field.Label>
                  )}
                  <input id="card_photo" type="file" style={{ display: 'none' }} {...register('card_photo')} />
               </Flex>
            </Field.Root>
            <Field.Root>
               <Field.Label textStyle="xs" color="white" mb="1">
                  Upload your Summary Care Record
                  <Box as="span" color="error">
                     *
                  </Box>
                  <Tooltip
                     content={<Box color="red.500">{errors.summary_care?.message as string}</Box>}
                     open={!!errors.summary_care?.message}
                  >
                     <InfoIcon fill="#F5E876" width={16} height={16} />
                  </Tooltip>
               </Field.Label>
               <Flex
                  alignItems="center"
                  justifyContent="space-between"
                  border="2px dashed gray"
                  p={3}
                  borderRadius="lg"
                  color="white"
                  w="full"
               >
                  <Flex css={signupStyles.upload_btn_box} alignItems="center" gap="3">
                     <Image src="/assets/icons/docs_icon.svg" alt="user icon" width="21" height="21" />
                     <Text fontSize="sm">Summary Care</Text>
                  </Flex>
                  {summary_care_url ? (
                     <Box display="flex" gap="3" alignItems="center">
                        {!errors.summary_care?.message && (
                           <Button
                              bg="brand"
                              textStyle="xs"
                              color="black"
                              fontWeight="bold"
                              borderRadius="12px"
                              h="8"
                              maxW="4"
                              minW="4"
                              px="6"
                              onClick={() => handleView(true, 1)}
                           >
                              View
                           </Button>
                        )}
                        <IconButton
                           height="40px"
                           rounded="full"
                           bg="#B5B5B5"
                           color="white"
                           onClick={() => resetField('summary_care')}
                        >
                           <HiTrash />
                        </IconButton>
                     </Box>
                  ) : (
                     <Field.Label
                        color="black"
                        textStyle="xs"
                        fontWeight="bold"
                        h="7"
                        maxW="20"
                        minW="20"
                        p="0"
                        bg="brand"
                        rounded="12px"
                        justifyContent="center"
                        htmlFor="summary_care"
                        cursor="pointer"
                     >
                        Upload
                     </Field.Label>
                  )}
                  <input id="summary_care" type="file" style={{ display: 'none' }} {...register('summary_care')} />
               </Flex>
            </Field.Root>

            <Field.Root>
               <Field.Label textStyle="xs" color="white" mb="1">
                  <Tooltip
                     positioning={{ placement: 'bottom-start' }}
                     open={!!errors.medical_certificate?.message}
                     content={<Box color="red.500">{errors.medical_certificate?.message as string}</Box>}
                  >
                     <Box display="flex" gap="1" alignItems="center">
                        Upload Medical Cannabis Prescription
                        <Box as="span" color="error"></Box>
                     </Box>
                  </Tooltip>
               </Field.Label>
               <Flex
                  alignItems="center"
                  justifyContent="space-between"
                  border="2px dashed gray"
                  p={3}
                  borderRadius="lg"
                  color="white"
                  w="full"
               >
                  <Flex css={signupStyles.upload_btn_box} alignItems="center" gap="3">
                     <Image src="/assets/icons/docs_icon.svg" alt="user icon" width="21" height="21" />
                     <Text fontSize="sm">Prescription</Text>
                  </Flex>
                  {medical_certificate_url ? (
                     <Box display="flex" gap="3" alignItems="center">
                        {!errors.medical_certificate?.message && (
                           <Button
                              bg="brand"
                              textStyle="xs"
                              color="black"
                              borderRadius="12px"
                              h="8"
                              maxW="4"
                              minW="4"
                              px="6"
                              fontWeight="bold"
                              onClick={() => handleView(true, 2)}
                           >
                              View
                           </Button>
                        )}
                        <IconButton
                           height="40px"
                           rounded="full"
                           bg="#B5B5B5"
                           color="white"
                           onClick={() => resetField('medical_certificate')}
                        >
                           <HiTrash />
                        </IconButton>
                     </Box>
                  ) : (
                     <Field.Label
                        color="black"
                        textStyle="xs"
                        fontWeight="bold"
                        h="7"
                        maxW="20"
                        minW="20"
                        p="0"
                        bg="brand"
                        rounded="12px"
                        justifyContent="center"
                        htmlFor="medical_certificate"
                        cursor="pointer"
                     >
                        Upload
                     </Field.Label>
                  )}
                  <input
                     id="medical_certificate"
                     type="file"
                     style={{ display: 'none' }}
                     {...register('medical_certificate')}
                  />
               </Flex>
            </Field.Root>

            <Field.Root invalid={!!errors.qualifying_condition}>
               <Field.Label textStyle="xs" color="white" mb="1">
                  Qualifying Condition
                  <Box as="span" color="error">
                     *
                  </Box>
               </Field.Label>
               <Input
                  placeholder="Qualifying Condition"
                  variant="subtle"
                  h="10"
                  rounded="12px"
                  bg="#202020"
                  {...register('qualifying_condition')}
               />
               <Field.ErrorText>{errors.qualifying_condition?.message}</Field.ErrorText>
            </Field.Root>
            <Box css={signupStyles.signup_btn_con}>
               <Button
                  onClick={() => reset()}
                  bg="darkerBtn"
                  textStyle="xs"
                  color="white"
                  h="10"
                  maxW="36"
                  minW="36"
                  p="0"
               >
                  Reset
               </Button>
               <Button type="submit" textStyle="xs" h="10" maxW="36" minW="36" p="0" disabled={isSubmitting}>
                  Submit
               </Button>
            </Box>
         </Stack>
         <Dialog.Root open={viewModal} onOpenChange={() => setViewModal(false)}>
            <Portal>
               <Dialog.Backdrop />
               <Dialog.Positioner>
                  <Dialog.Content bg="transparent">
                     <Dialog.Body px="0" pt="100px">
                        <Box textAlign="center">
                           {/* <Image src={viewItem.src} alt={viewItem.alt} borderRadius="lg" mx="auto" mb="4" /> */}
                           {viewItem.src ? (
                              viewItem.src.endsWith('.pdf') || viewItem.alt === 'summary_care' ? (
                                 // PDF viewer
                                 <iframe
                                    src={viewItem.src}
                                    width="100%"
                                    height="600px"
                                    style={{ borderRadius: '8px', border: 'none' }}
                                 />
                              ) : (
                                 // Image viewer
                                 <Image
                                    src={viewItem.src}
                                    alt={viewItem.alt}
                                    borderRadius="lg"
                                    mx="auto"
                                    maxH="600px"
                                    objectFit="contain"
                                 />
                              )
                           ) : (
                              <Text color="white">No preview available</Text>
                           )}
                           <Box css={viewCardStyles.carousel_arrow_area} display="flex" justifyContent="center" gap="4">
                              <IconButton
                                 size="sm"
                                 onClick={() => prevFn()}
                                 disabled={!carouselItems[carouselIndex - 1]?.src}
                              >
                                 <MdKeyboardArrowLeft />
                              </IconButton>
                              <IconButton
                                 size="sm"
                                 onClick={() => nextFn()}
                                 disabled={!carouselItems[carouselIndex + 1]?.src}
                              >
                                 <MdOutlineKeyboardArrowRight />
                              </IconButton>
                           </Box>
                        </Box>
                     </Dialog.Body>
                  </Dialog.Content>
               </Dialog.Positioner>
            </Portal>
         </Dialog.Root>

         {/* <Dialog.Root open={openSupportModal} onOpenChange={() => handleSupportModalClose()}>
            <Portal>
               <Dialog.Backdrop />
               <Dialog.Positioner>
                  <Dialog.Content w="350px">
                     <Dialog.Body px="0" pt="50px">
                        <Box textAlign="center">
                           <Text marginBottom="8" textStyle="lg" color="black">
                              Phone number:{' '}
                              <Link href="tel:03334434433" color="blue.500" _hover={{ textDecoration: 'underline' }}>
                                 0333 443 4433
                              </Link>
                           </Text>
                           <Button
                              onClick={handleSupportModalClose}
                              bg="darkerBtn"
                              textStyle="xs"
                              color="white"
                              h="10"
                              maxW="36"
                              minW="36"
                              p="0"
                           >
                              Cancel
                           </Button>
                        </Box>
                     </Dialog.Body>
                  </Dialog.Content>
               </Dialog.Positioner>
            </Portal>
         </Dialog.Root> */}
      </Box>
   );
};

export default SubmitApplicationForm;
